import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { useAuth } from "./AuthContext";
import { getTourSteps } from "../components/Tour/tourSteps";

const STORAGE_PREFIX = "pd_seen_tour_v1_";

const TourContext = createContext(null);

export const TourProvider = ({ children }) => {
  const { user } = useAuth();
  const [isActive, setIsActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const steps = useMemo(() => getTourSteps(user?.role), [user?.role]);

  const hasSeenTour = useCallback(() => {
    if (!user) return true;
    return !!localStorage.getItem(STORAGE_PREFIX + user.id);
  }, [user]);

  const markSeen = useCallback(() => {
    if (!user) return;
    localStorage.setItem(STORAGE_PREFIX + user.id, "1");
  }, [user]);

  const startTour = useCallback(() => {
    setStepIndex(0);
    setIsActive(true);
  }, []);

  const stopTour = useCallback(() => {
    setIsActive(false);
    markSeen();
  }, [markSeen]);

  const next = useCallback(() => {
    setStepIndex((i) => {
      if (i + 1 >= steps.length) {
        setIsActive(false);
        markSeen();
        return i;
      }
      return i + 1;
    });
  }, [steps.length, markSeen]);

  const previous = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const value = {
    isActive,
    stepIndex,
    steps,
    startTour,
    stopTour,
    next,
    previous,
    hasSeenTour,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

export const useTour = () => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used within a TourProvider");
  return ctx;
};

export default TourContext;
